import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useIsBizyEnabledForUser } from '@bonusly/who-am-i';

import { resolvePromptLibraryKey } from '../lib/prompt-library';
import { useBizyExternalOpenStore } from '../store/use-bizy-external-open-store';
import { useBizyPageContextStore } from '../store/use-bizy-page-context-store';
import { BIZY_DEEP_LINK_TRIGGER, useBizySidePanelStore } from '../store/use-bizy-side-panel-store';

export const BIZY_DEEP_LINK_OPEN_PARAM = 'bizy';
export const BIZY_DEEP_LINK_PROMPT_PARAM = 'bizy_prompt';
export const BIZY_DEEP_LINK_PROMPT_KEY_PARAM = 'bizy_prompt_key';
export const BIZY_DEEP_LINK_PROMPT_MODE_PARAM = 'bizy_prompt_mode';
export const BIZY_DEEP_LINK_CONVERSATION_PARAM = 'bizy_conversation';

const DEEP_LINK_PARAMS = [
	BIZY_DEEP_LINK_OPEN_PARAM,
	BIZY_DEEP_LINK_PROMPT_PARAM,
	BIZY_DEEP_LINK_PROMPT_KEY_PARAM,
	BIZY_DEEP_LINK_PROMPT_MODE_PARAM,
	BIZY_DEEP_LINK_CONVERSATION_PARAM,
] as const;

// Cap the auto-sent prompt: a deep link sends this to the LLM on the user's
// behalf, so keep email/campaign links from injecting an unbounded payload.
const MAX_PROMPT_LENGTH = 2000;

export interface BizyDeepLinkIntent {
	open: boolean;
	fullscreen: boolean;
	/** Free-text prompt (`?bizy_prompt=`). Takes precedence over promptKey. */
	prompt?: string;
	/**
	 * Curated prompt reference (`?bizy_prompt_key=`): a library-relative key like
	 * `prepFollowUp.needsAgenda` (the part under `promptLibrary.library.`).
	 * Resolved + auto-translated at consume time, so the URL stays short and
	 * locale-correct. Limited to the curated `library` subtree
	 */
	promptKey?: string;
	promptMode: 'send' | 'prefill';
	conversationId?: string;
}

export function parseBizyDeepLink(search: string): BizyDeepLinkIntent | undefined {
	const params = new URLSearchParams(search);
	const openValue = params.get(BIZY_DEEP_LINK_OPEN_PARAM);
	const rawPrompt = params.get(BIZY_DEEP_LINK_PROMPT_PARAM)?.trim();
	const promptKey = params.get(BIZY_DEEP_LINK_PROMPT_KEY_PARAM)?.trim() || undefined;
	const conversationId = params.get(BIZY_DEEP_LINK_CONVERSATION_PARAM)?.trim() || undefined;
	const prompt = rawPrompt ? rawPrompt.slice(0, MAX_PROMPT_LENGTH) : undefined;

	const wantsOpen = openValue === 'open' || openValue === 'fullscreen';
	// Any of these intents implies opening the panel.
	if (!wantsOpen && !prompt && !promptKey && !conversationId) return undefined;

	const promptMode = params.get(BIZY_DEEP_LINK_PROMPT_MODE_PARAM) === 'prefill' ? 'prefill' : 'send';

	return {
		open: true,
		fullscreen: openValue === 'fullscreen',
		prompt,
		promptKey,
		promptMode,
		conversationId,
	};
}

/**
 * Deep link into Bizy chat from any URL (RR-171), mirroring
 * {@link useBizySettingsDeepLink}. Supported params, all composable:
 *
 * - `?bizy=open` / `?bizy=fullscreen` — open the panel (side panel vs fullscreen)
 * - `?bizy_prompt=<text>` — starter message
 * - `?bizy_prompt_mode=send|prefill` — auto-send (default) vs drop into the input
 * - `?bizy_conversation=<id>` — load an existing conversation; combinable with a
 *   prompt to add a follow-up into it
 *
 * Opens the panel, queues the prompt/conversation through the external-open
 * pipeline (consumed by {@link useBizyExternalOpenAutoSend}), then strips the
 * params via history.replaceState so refreshes and back/forward navigation
 * don't re-trigger it. Backend authorization on the conversation fetch is what
 * keeps `?bizy_conversation=` from exposing other users' conversations.
 *
 * Mount this LAST among the Bizy layout effects so its open wins over the
 * route-aware auto-open/close and drop-to-side-panel effects on mount.
 */
export function useBizyDeepLink() {
	const isBizyEnabledForUser = useIsBizyEnabledForUser();
	const { t } = useTranslation('admin_bizy');

	useEffect(() => {
		if (!isBizyEnabledForUser) return;

		const intent = parseBizyDeepLink(window.location.search);
		if (!intent) return;

		// Free text wins; otherwise resolve a curated prompt key to translated text.
		let starterMessage = intent.prompt;
		if (!starterMessage && intent.promptKey) {
			const key = resolvePromptLibraryKey(intent.promptKey);
			if (key) {
				const text = t(key);
				// `t` returns the key unchanged when the namespace hasn't loaded
				// yet. Bail WITHOUT stripping params so a later `t` update re-runs
				// this effect and resolves the prompt rather than losing it.
				if (!text || text === key) return;
				starterMessage = text;
			}
			// Unknown key: fall through — open the panel, drop the param.
		}

		if (starterMessage || intent.conversationId) {
			useBizyExternalOpenStore.getState().setPendingRequest({
				source: 'url_deep_link',
				pagePath: window.location.pathname,
				pageContext: useBizyPageContextStore.getState().context.pageType,
				...(starterMessage ? { starterMessage } : {}),
				...(intent.conversationId ? { conversationId: intent.conversationId } : {}),
				autoSend: intent.promptMode === 'send',
				// A bare prompt is a new topic — start fresh, no dialog.
				// `?bizy_conversation=` targets a specific conversation instead.
				forceNewConversation: Boolean(starterMessage) && !intent.conversationId,
			});
		}

		const store = useBizySidePanelStore.getState();
		store.showChat();
		store.open(BIZY_DEEP_LINK_TRIGGER, { isFullscreen: intent.fullscreen });

		const url = new URL(window.location.href);
		for (const param of DEEP_LINK_PARAMS) url.searchParams.delete(param);
		window.history.replaceState(window.history.state, '', url);

		// The deep-link trigger makes close('auto') and the fullscreen-drop effects
		// no-op so the same-mount route-aware effects can't undo this open before it lands.
		// Release it on the next macrotask so normal route-driven auto-close / fullscreen-drop
		// resume instead of staying disabled for the rest of the session.
		// A user interaction in the meantime overwrites lastTrigger, so we only release if it's still ours.
		setTimeout(() => {
			if (useBizySidePanelStore.getState().lastTrigger === BIZY_DEEP_LINK_TRIGGER) {
				useBizySidePanelStore.setState({ lastTrigger: 'auto' });
			}
		}, 0);
	}, [isBizyEnabledForUser, t]);
}
